import { useEffect, useState, useCallback, useMemo } from 'react';
import CollapsibleTable from '../../components/table';
import ModalPerfiles from '../../components/ModalPerfiles';
import DoubleConfirmModal from '../../components/DoubleConfirmModal';
import { useSession } from '../../components/hooks/useSession';
import handleOpenSweetAlert from '../../components/handleOpenSweetAlert';
import config from '../../config/config';
import axios from 'axios';

import EditIcon from '@mui/icons-material/Edit';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BotonAgregar from '../../components/BotonAgregar';

import ExportButton from '../../components/BotonEx';
import { Box } from '@mui/material';

// Helpers sesión
const getSessionString = (key) => sessionStorage.getItem(key) || '';

const getSessionArray = (key) => {
  try {
    const value = sessionStorage.getItem(key);
    if (!value) return [];
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    console.error('Error parseando', key, e);
    return [];
  }
};

// Normaliza idEmpresa que puede venir como:
//  - [2]
//  - "2"
//  - "[1,2,3]"
const normalizeEmpresaArray = (idEmpresa) => {
  if (idEmpresa == null) return [];

  if (Array.isArray(idEmpresa)) {
    return idEmpresa.map((x) => Number(x)).filter((n) => Number.isFinite(n) && n > 0);
  }

  if (typeof idEmpresa === 'string') {
    const s = idEmpresa.trim();

    if (s.startsWith('[') && s.endsWith(']')) {
      try {
        const arr = JSON.parse(s);
        if (Array.isArray(arr)) {
          return arr.map((x) => Number(x)).filter((n) => Number.isFinite(n) && n > 0);
        }
      } catch {}
    }

    const n = Number(s);
    return Number.isFinite(n) && n > 0 ? [n] : [];
  }

  const n = Number(idEmpresa);
  return Number.isFinite(n) && n > 0 ? [n] : [];
};

export default function Perfiles() {
  const apiUrl = config.apiBaseUrl;

  const sessionData = useSession();
  const { idPerfil } = sessionData;

  // ─── Datos de sesión reactivos ────────────────────────────────────────
  const [loggedInUsername, setLoggedInUsername] = useState(getSessionString('loggedInUsername'));
  const [idEmpresa, setIdEmpresa] = useState(getSessionArray('EmpresaId'));
  const [nombreEmpresa, setNombreEmpresa] = useState(getSessionString('EmpresaNombre'));
  const [nombrePerfil, setNombrePerfil] = useState(getSessionString('PerfilNombre') || '');

  // UsuarioId para persistencia
  const usuarioId = useMemo(() => Number(sessionStorage.getItem('UsuarioId') || 0), []);

  // Key estable
  const empresaIdsKey = useMemo(() => {
    const arr = normalizeEmpresaArray(idEmpresa);
    return arr.length ? arr.join(',') : '';
  }, [idEmpresa]);

  const empresaIds = useMemo(() => {
    if (!empresaIdsKey) return [];
    return empresaIdsKey
      .split(',')
      .map((n) => Number(n))
      .filter((x) => Number.isFinite(x) && x > 0);
  }, [empresaIdsKey]);

  // Para preferencias: TODAS => 0
  const empresaIdPref = useMemo(() => {
    return empresaIds.length > 1 ? 0 : (empresaIds[0] ?? 0);
  }, [empresaIds]);

  // ─── Escucha cambio de empresa ────────────────────────────────────────
  useEffect(() => {
    const handleEmpresaChange = () => {
      const nuevoUser = getSessionString('loggedInUsername');
      const nuevosIds = getSessionArray('EmpresaId');
      const nuevaEmpresa = getSessionString('EmpresaNombre');
      const nuevoPerfil = getSessionString('PerfilNombre') || '';

      setLoggedInUsername(nuevoUser);
      setIdEmpresa(nuevosIds);
      setNombreEmpresa(nuevaEmpresa);
      setNombrePerfil(nuevoPerfil);
    };

    window.addEventListener('empresaCambiada', handleEmpresaChange);
    return () => window.removeEventListener('empresaCambiada', handleEmpresaChange);
  }, []);

  const [data, setData] = useState([]);
  const [formData, setFormData] = useState({ PERFIL: '', SESSION: loggedInUsername });
  const [selectedUser, setSelectedUser] = useState(null);
  const [permissionModalOpen, setPermissionModalOpen] = useState(false);
  const [confirmModal, setConfirmModal] = useState({
    open: false,
    message: '',
    action: '',
    row: null,
  });

  // ───────────── Obtener datos ─────────────
  const fetchData = useCallback(async () => {
    if (!apiUrl) return;
    if (!empresaIdsKey) return;

    try {
      const { data: resData } = await axios.post(`${apiUrl}/perfilesTabla`, {
        nombrePerfil,
        nombreEmpresa,
        idEmpresa: empresaIds,
        idPerfil
      });

      if (resData.mensaje) {
        handleOpenSweetAlert(resData.mensaje, 'error');
        setData([]);
      } else {
        setData(resData);
      }
    } catch (err) {
      const msg =
        err.response?.data?.error ||
        err.response?.data?.mensaje ||
        'Error al procesar la solicitud';
      handleOpenSweetAlert(msg, 'error');
    }
  }, [apiUrl, nombrePerfil, nombreEmpresa, empresaIds, empresaIdsKey, idPerfil]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ───────────── Abrir Modal Editar Permisos ─────────────
  const handlePermisosUsuario = useCallback((row) => {
    if (!row?.PERFIL) {
      handleOpenSweetAlert('Error: Datos de usuario inválidos', 'error');
      return;
    }

    setSelectedUser({ USUARIOID: row.ID, USER_NAME: row.PERFIL });
    setFormData({ PERFIL: row.PERFIL || '', SESSION: loggedInUsername });
    setPermissionModalOpen(true);
  }, [loggedInUsername]);

  const handleInputChange = (fieldName, value) => {
    setFormData((prev) => ({ ...prev, [fieldName]: value }));
  };

  const cerrarPermisosUsuario = () => {
    setPermissionModalOpen(false);
    setSelectedUser(null);
    setFormData({ PERFIL: '', SESSION: loggedInUsername });
  };

  // ───────────── Activar / Desactivar perfil ─────────────
  const cambiarEstadoPerfil = useCallback(async (row) => {
    const nuevoEstado = row.ACTIVO ? 0 : 1;
    try {
      await axios.post(`${apiUrl}/desactivarPerfil`, {
        TIPOUSERID: row.ID,
        ACTIVO: nuevoEstado,
        SESSION: loggedInUsername,
      });

      handleOpenSweetAlert(
        nuevoEstado ? 'Perfil activado con éxito' : 'Perfil desactivado con éxito',
        'success'
      );
      fetchData();
    } catch (err) {
      handleOpenSweetAlert('Hubo un error al actualizar el perfil.', 'error');
    }
  }, [apiUrl, fetchData, loggedInUsername]);

  const openConfirmation = useCallback((action, row, message) => {
    setConfirmModal({ open: true, action, row, message });
  }, []);

  const handleConfirmAction = () => {
    if (confirmModal.action === 'cambiarEstado') {
      cambiarEstadoPerfil(confirmModal.row);
    }
    setConfirmModal({ open: false, action: '', row: null, message: '' });
  };

  const handleCancelAction = () => {
    setConfirmModal({ open: false, action: '', row: null, message: '' });
  };

  // ───────────── Botones por fila ─────────────
  const buttons = useMemo(() => [
    {
      id: 1,
      label: 'Editar',
      onClick: handlePermisosUsuario,
      icon: <EditIcon />,
      buttonColor: '#2196f3',
      buttonFontColor: '#ffffff',
      isVisible: true,
      isDisabled: false,
    },
    {
      id: 2,
      label: (row) => row.ACTIVO ? 'Desactivar' : 'Activar',
      onClick: (row) => {
        const mensaje = row.ACTIVO
          ? '¿Está seguro de desactivar este perfil?'
          : '¿Está seguro de activar este perfil?';
        openConfirmation('cambiarEstado', row, mensaje);
      },
      icon: (row) => row.ACTIVO ? <BlockIcon /> : <CheckCircleIcon />,
      buttonColor: (row) => row.ACTIVO ? '#e53935' : '#43a047',
      buttonFontColor: '#ffffff',
      isVisible: true,
      isDisabled: false,
    },
  ], [handlePermisosUsuario, openConfirmation]);

  // ───────────── Render ─────────────
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', p: 'clamp(6px, 0.8vw, 12px)', bgcolor: '#f8fafc' }}>
      <Box sx={{ flexGrow: 1, overflow: 'hidden', minHeight: 0 }}>
      <CollapsibleTable
        buttons={buttons}
        dataTable={data}
        hiddenFields={['FECHACREACION']}
        density="small"
        compactActions
        maxInlineActions={2}
        showExportButton={true}
        rightButtons={
          <ExportButton
            data={data}
            fileName="Perfiles.xlsx"
            columnMap={[
              { key: 'ID', label: 'ID' },
              { key: 'PERFIL', label: 'PERFIL' },
              { key: 'ACTIVO', label: 'ACTIVO' },
            ]}
          />
        }
        leftButtons={
          <BotonAgregar
            title="Agregar Perfil"
            onClick={() => {
              setSelectedUser({ PERFILID: 0 });
              setPermissionModalOpen(true);
            }}
          />
        }

        persistEnabled
        persistKey="Perfiles"
        persistVista="layout"
        usuarioId={usuarioId}
        empresaId={empresaIdPref}
        apiUrl={apiUrl}
      />
      </Box>

      <ModalPerfiles
        key={selectedUser?.USUARIOID || 'nuevo'}
        open={permissionModalOpen}
        onClose={cerrarPermisosUsuario}
        onSave={fetchData}
        userId={selectedUser?.USUARIOID}
        userName={selectedUser?.USER_NAME}
        formData={formData}
        handleInputChange={handleInputChange}
      />

      <DoubleConfirmModal
        open={confirmModal.open}
        message={confirmModal.message}
        handleConfirm={handleConfirmAction}
        handleCancel={handleCancelAction}
      />
    </Box>
  );
}